import { getFirestore, collection, query, where, orderBy, getDocs, startAfter, limit, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { matchService } from './matchService';

const CONVERSATIONS_PER_PAGE = 10;

export const messageService = {
    // Fetch user details helper function
    async fetchUserDetails(uid) {
        try {
            const data = await matchService.getUserDetails(uid);
            return {
                name: data.name,
                avatar: data.photos?.[0]?.photoUrl
            };
        } catch (error) {
            console.error('Error fetching user details:', error);
            return { name: 'User', avatar: null };
        }
    },

    // Subscribe to conversations
    subscribeToConversations(userId, callback) {
        const db = getFirestore();
        const conversationsRef = collection(db, 'Conversations');
        
        const q = query(
            conversationsRef,
            where('members', 'array-contains', userId),
            orderBy('updateAt', 'desc'),
            limit(CONVERSATIONS_PER_PAGE)
        );

        let initialLoad = true;
        let conversationsMap = new Map();

        // Return the unsubscribe function
        return onSnapshot(q, async (snapshot) => {
            try {
                const changes = [];

                if (initialLoad) {
                    // Premier chargement : traiter tous les documents
                    for (const doc of snapshot.docs) {
                        const conversationData = doc.data();
                        const otherUserId = conversationData.members.find(id => id !== userId);
                        const userDetails = await this.fetchUserDetails(otherUserId);
                        const isUnread = this.isConversationUnread(conversationData, userId);

                        conversationsMap.set(doc.id, {
                            id: doc.id,
                            ...conversationData,
                            isUnread,
                            otherUser: {
                                uid: otherUserId,
                                ...userDetails
                            }
                        });
                    }
                    initialLoad = false;
                }

                // Traiter uniquement les changements
                for (const change of snapshot.docChanges()) {
                    const conversationData = change.doc.data();
                    const otherUserId = conversationData.members.find(id => id !== userId);
                    const userDetails = await this.fetchUserDetails(otherUserId);
                    const isUnread = this.isConversationUnread(conversationData, userId);

                    const conversationWithUser = {
                        id: change.doc.id,
                        ...conversationData,
                        isUnread,
                        otherUser: {
                            uid: otherUserId,
                            ...userDetails
                        }
                    };

                    // Mettre à jour la map selon le type de changement
                    if (change.type === 'added' || change.type === 'modified') {
                        conversationsMap.set(change.doc.id, conversationWithUser);
                    } else if (change.type === 'removed') {
                        conversationsMap.delete(change.doc.id);
                    }

                    changes.push({
                        type: change.type,
                        conversation: conversationWithUser
                    });
                }

                // Get the last visible document
                const lastVisible = snapshot.docs[snapshot.docs.length - 1];
                const hasMore = snapshot.docs.length === CONVERSATIONS_PER_PAGE;

                // Le champ écrit par chatService.sendMessage est "updateAt" (nombre unix),
                // pas "updatedAt.seconds" (Timestamp) : l'ancien tri comparait toujours 0-0
                // et ne triait donc jamais réellement les conversations.
                const sortedConversations = Array.from(conversationsMap.values())
                    .filter(conv => conv.latestMessage !== null)
                    .sort((a, b) => (b.updateAt || 0) - (a.updateAt || 0));


                callback({
                    changes,
                    lastDoc: lastVisible,
                    hasMore,
                    allConversations: sortedConversations
                });

            } catch (error) {
                console.error("Error in subscribeToConversations:", error);
            }
        });
    },

    // Helper function to check if a conversation is unread
    isConversationUnread(conversationData, userId) {
        // If there's no latest message, it can't be unread
        if (!conversationData.latestMessage) return false;
        
        // If the latest message is from the current user, it's not unread
        if (conversationData.latestMessage.user?._id === userId) return false;
        
        // If there's no readBy field or no timestamp for this user, it's unread
        if (!conversationData.readBy || !conversationData.readBy[userId]) return true;
        
        // Compare timestamps to see if the message is newer than la derniere lecture.
        // readBy est maintenant un int64 (unix seconds), comme latestMessage.createdAt.
        // Ancien format (Firestore Timestamp avec .seconds) gere pour les conversations
        // existantes dont readBy n'a pas encore ete reecrit avec le nouveau format.
        const readByValue = conversationData.readBy[userId];
        const lastReadTimestamp = typeof readByValue === 'number' ? readByValue : readByValue.seconds;
        const messageTimestamp = conversationData.latestMessage.createdAt;

        return lastReadTimestamp < messageTimestamp;
    },

    // Mark conversation as read
    async markConversationAsRead(conversationId, userId) {
        try {
            const db = getFirestore();
            const conversationRef = doc(db, 'Conversations', conversationId);

            // Update the readBy field avec un timestamp client (unix seconds), pour
            // rester sur la meme horloge que latestMessage.createdAt (ecrit par
            // chatService.sendMessage avec moment().utc().unix()). Melanger serverTimestamp()
            // (horloge serveur) et un timestamp client provoquait un decalage d'horloge
            // qui pouvait marquer un message comme "lu" avant meme qu'il soit vu.
            await updateDoc(conversationRef, {
                [`readBy.${userId}`]: Math.floor(Date.now() / 1000)
            });
        } catch (error) {
            console.error('Error marking conversation as read:', error);
            throw error;
        }
    },

    // Fetch initial conversations
    async fetchConversations(userId) {
        const db = getFirestore();
        const conversationsRef = collection(db, 'Conversations');

        try {
            const q = query(
                conversationsRef,
                where('members', 'array-contains', userId),
                orderBy('updateAt', 'desc'),
                limit(CONVERSATIONS_PER_PAGE)
            );

            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const lastVisible = snapshot.docs[snapshot.docs.length - 1];
                const conversationsPromises = snapshot.docs.map(async (doc) => {
                    const conversationData = doc.data();
                    const otherUserId = conversationData.members.find(id => id !== userId);
                    const userDetails = await this.fetchUserDetails(otherUserId);
                    
                    const isUnread = this.isConversationUnread(conversationData, userId);

                    return {
                        id: doc.id,
                        ...conversationData,
                        isUnread,
                        otherUser: {
                            uid: otherUserId,
                            ...userDetails
                        }
                    };
                });

                const conversationsWithUsers = await Promise.all(conversationsPromises);
                const conversationsWithMessages = conversationsWithUsers.filter(conv => conv.latestMessage !== null);
                
                return {
                    conversations: conversationsWithMessages,
                    lastDoc: lastVisible,
                    hasMore: snapshot.docs.length === CONVERSATIONS_PER_PAGE
                };
            }
            
            return {
                conversations: [],
                lastDoc: null,
                hasMore: false
            };
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    },

    // Load more conversations
    async loadMoreConversations(userId, lastDoc) {
        const db = getFirestore();
        const conversationsRef = collection(db, 'Conversations');

        try {
            const q = query(
                conversationsRef,
                where('members', 'array-contains', userId),
                orderBy('updateAt', 'desc'),
                startAfter(lastDoc),
                limit(CONVERSATIONS_PER_PAGE)
            );
            
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const lastVisible = snapshot.docs[snapshot.docs.length - 1];
                const conversationsPromises = snapshot.docs.map(async (doc) => {
                const conversationData = doc.data();
                const otherUserId = conversationData.members.find(id => id !== userId);
                const userDetails = await this.fetchUserDetails(otherUserId);
                
                const isUnread = this.isConversationUnread(conversationData, userId);
                
                    return {
                    id: doc.id,
                    ...conversationData,
                        isUnread,
                        otherUser: {
                            uid: otherUserId,
                            ...userDetails
                        }
                    };
                });

                const newConversationsWithUsers = await Promise.all(conversationsPromises);
                const newConversationsWithMessages = newConversationsWithUsers.filter(conv => conv.latestMessage !== null);
                
                return {
                    conversations: newConversationsWithMessages,
                    lastDoc: lastVisible,
                    hasMore: snapshot.docs.length === CONVERSATIONS_PER_PAGE
                };
            }
            
            return {
                conversations: [],
                lastDoc: null,
                hasMore: false
            };
        } catch (error) {
            console.error('Error loading more conversations:', error);
            throw error;
        }
    }
};
